import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  UserCheck,
  Search,
  CheckCircle2,
  XCircle,
  Building2,
  MapPin,
  Calendar,
  Phone,
  Mail,
  Send,
  Shield,
  ChevronLeft,
  ChevronRight,
  FileText,
  AlertCircle,
  Clock,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ArtisanDocument {
  id: string;
  type: string;
  fileUrl: string;
  status: string;
  uploadedAt: string;
  expiresAt: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

interface PendingVerificationItem {
  id: string;
  companyName: string;
  siret: string | null;
  verificationStatus: string;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
  };
  documents: ArtisanDocument[];
}

interface ArtisanItem {
  id: string;
  companyName: string;
  siret: string | null;
  specialties: unknown;
  serviceCity: string;
  serviceRadiusKm: number;
  yearsExperience: string;
  hasDecennale: string;
  pendingVerification: boolean;
  createdAt: string;
  User: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    createdAt: string;
  };
}

type VerificationStatus = "UNVERIFIED" | "PENDING" | "VERIFIED" | "REJECTED";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getSpecialtiesLabel(specialties: unknown): string {
  if (!Array.isArray(specialties) || specialties.length === 0) return "—";
  return (specialties as string[]).slice(0, 3).join(", ") + (specialties.length > 3 ? `…` : "");
}

function formatName(artisan: ArtisanItem): string {
  const u = artisan.User;
  if (u.firstName || u.lastName) {
    return [u.firstName, u.lastName].filter(Boolean).join(" ");
  }
  return artisan.companyName;
}

/** Derive a verification status from the ArtisanProfile boolean flag */
function deriveStatus(artisan: ArtisanItem): VerificationStatus {
  return artisan.pendingVerification ? "PENDING" : "VERIFIED";
}

// ─── VerificationStatusBadge ─────────────────────────────────────────────────

function VerificationStatusBadge({ status }: { status: VerificationStatus }) {
  switch (status) {
    case "VERIFIED":
      return (
        <Badge className="bg-success/10 text-success border-success/20 shrink-0">
          <CheckCircle2 className="h-3 w-3 mr-1" aria-hidden="true" />
          Vérifié
        </Badge>
      );
    case "PENDING":
      return (
        <Badge className="bg-warning/10 text-warning border-warning/20 shrink-0">
          <Clock className="h-3 w-3 mr-1" aria-hidden="true" />
          En attente
        </Badge>
      );
    case "REJECTED":
      return (
        <Badge className="bg-destructive/10 text-destructive border-destructive/20 shrink-0">
          <XCircle className="h-3 w-3 mr-1" aria-hidden="true" />
          Rejeté
        </Badge>
      );
    case "UNVERIFIED":
    default:
      return (
        <Badge className="bg-muted text-muted-foreground border-border shrink-0">
          <AlertCircle className="h-3 w-3 mr-1" aria-hidden="true" />
          Non vérifié
        </Badge>
      );
  }
}

// ─── Document type labels ─────────────────────────────────────────────────────

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  SIRET: "Extrait SIRET",
  KBIS: "Extrait Kbis",
  ASSURANCE_DECENNALE: "Assurance décennale",
  RGE: "Certificat RGE",
};

function documentStatusBadge(status: string) {
  if (status === "APPROVED")
    return (
      <Badge className="bg-success/10 text-success border-success/20 text-xs">
        <CheckCircle2 className="h-3 w-3 mr-1" />Approuvé
      </Badge>
    );
  if (status === "REJECTED")
    return (
      <Badge className="bg-destructive/10 text-destructive border-destructive/20 text-xs">
        <XCircle className="h-3 w-3 mr-1" />Rejeté
      </Badge>
    );
  return (
    <Badge className="bg-warning/10 text-warning border-warning/20 text-xs">
      <Clock className="h-3 w-3 mr-1" />En attente
    </Badge>
  );
}

// ─── DocumentVerificationCard ─────────────────────────────────────────────────

function DocumentVerificationCard({
  item,
  canAct,
  onApproveDoc,
  onRejectDoc,
  isActing,
}: {
  item: PendingVerificationItem;
  canAct: boolean;
  onApproveDoc: (documentId: string) => void;
  onRejectDoc: (doc: ArtisanDocument, artisanName: string) => void;
  isActing: boolean;
}) {
  const name = item.user.firstName || item.user.lastName
    ? [item.user.firstName, item.user.lastName].filter(Boolean).join(" ")
    : item.companyName;

  const vsStatus = item.verificationStatus as VerificationStatus;

  return (
    <Card>
      <CardContent className="py-5 px-5">
        {/* Artisan header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
              {(item.companyName[0] ?? "A").toUpperCase()}
            </div>
            <div>
              <div className="font-medium text-sm">{name}</div>
              <div className="text-xs text-muted-foreground">{item.companyName}</div>
              <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                <Mail aria-hidden="true" className="h-3 w-3 shrink-0" />
                {item.user.email}
              </div>
            </div>
          </div>
          <VerificationStatusBadge status={vsStatus} />
        </div>

        {/* Documents table */}
        <div className="rounded-lg border border-border divide-y divide-border">
          {item.documents.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 px-4 py-3">
              <FileText aria-hidden="true" className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">
                  {DOCUMENT_TYPE_LABELS[doc.type] ?? doc.type}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Soumis le{" "}
                  {new Date(doc.uploadedAt).toLocaleDateString("fr-FR", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                  {doc.expiresAt && (
                    <> · Expire le{" "}
                      {new Date(doc.expiresAt).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {documentStatusBadge(doc.status)}
                <a
                  href={doc.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Voir le document"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
                {canAct && doc.status === "PENDING" && (
                  <>
                    <Button
                      size="sm"
                      className="bg-success hover:bg-success/90 text-success-foreground h-7 px-2.5"
                      onClick={() => onApproveDoc(doc.id)}
                      disabled={isActing}
                      aria-label="Approuver ce document"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                      Approuver
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-destructive text-destructive hover:bg-destructive/5 h-7 px-2.5"
                      onClick={() => onRejectDoc(doc, name)}
                      disabled={isActing}
                      aria-label="Rejeter ce document"
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1" />
                      Rejeter
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
          {item.documents.length === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              Aucun document soumis
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── VerificationReviewCard ───────────────────────────────────────────────────

function VerificationReviewCard({
  artisan,
  canAct,
  onApprove,
  onReject,
  isActing,
}: {
  artisan: ArtisanItem;
  canAct: boolean;
  onApprove: (id: string) => void;
  onReject: (artisan: ArtisanItem) => void;
  isActing: boolean;
}) {
  const status = deriveStatus(artisan);

  // Derived document items from declarative profile data
  const documentItems = [
    {
      type: "SIRET",
      label: "Numéro SIRET",
      value: artisan.siret || null,
      present: !!artisan.siret,
    },
    {
      type: "ASSURANCE_DECENNALE",
      label: "Assurance décennale",
      value:
        artisan.hasDecennale === "YES"
          ? "Déclarée"
          : artisan.hasDecennale === "IN_PROGRESS"
          ? "En cours d'obtention"
          : "Non déclarée",
      present: artisan.hasDecennale === "YES" || artisan.hasDecennale === "IN_PROGRESS",
    },
    {
      type: "EXPERIENCE",
      label: "Années d'expérience déclarées",
      value: artisan.yearsExperience || "Non renseigné",
      present: !!artisan.yearsExperience,
    },
  ];

  const missingCount = documentItems.filter((d) => !d.present).length;

  return (
    <Card>
      <CardContent className="py-5 px-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
              {(artisan.companyName[0] ?? "A").toUpperCase()}
            </div>
            <div>
              <div className="font-medium text-sm">{formatName(artisan)}</div>
              <div className="text-xs text-muted-foreground">{artisan.companyName}</div>
              <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                <Mail aria-hidden="true" className="h-3 w-3 shrink-0" />
                {artisan.User.email}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <VerificationStatusBadge status={status} />
            {missingCount > 0 && (
              <span className="text-xs text-warning">
                {missingCount} élément{missingCount > 1 ? "s" : ""} manquant{missingCount > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Document items */}
        <div className="rounded-lg border border-border bg-muted/30 divide-y divide-border mb-4">
          {documentItems.map((doc) => (
            <div key={doc.type} className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2">
                <FileText
                  aria-hidden="true"
                  className={`h-3.5 w-3.5 ${doc.present ? "text-success" : "text-warning"}`}
                />
                <span className="text-xs font-medium">{doc.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{doc.value || "Non renseigné"}</span>
                {doc.present ? (
                  <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5 text-success shrink-0" />
                ) : (
                  <AlertCircle aria-hidden="true" className="h-3.5 w-3.5 text-warning shrink-0" />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Context row */}
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground mb-4">
          <span className="flex items-center gap-1">
            <MapPin aria-hidden="true" className="h-3 w-3" />
            {artisan.serviceCity} (+{artisan.serviceRadiusKm} km)
          </span>
          <span className="flex items-center gap-1">
            <Building2 aria-hidden="true" className="h-3 w-3" />
            {getSpecialtiesLabel(artisan.specialties)}
          </span>
          <span className="flex items-center gap-1">
            <Calendar aria-hidden="true" className="h-3 w-3" />
            Inscrit le{" "}
            {new Date(artisan.User.createdAt).toLocaleDateString("fr-FR", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
          {artisan.User.phone && (
            <span className="flex items-center gap-1">
              <Phone aria-hidden="true" className="h-3 w-3" />
              {artisan.User.phone}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Link to={`/travauxbtp/artisans/${artisan.id}`}>
            <Button size="sm" variant="outline" className="h-8">
              <ExternalLink aria-hidden="true" className="h-3.5 w-3.5 mr-1.5" />
              Voir la fiche
            </Button>
          </Link>
          {canAct && (
            <>
              <Button
                size="sm"
                className="bg-success hover:bg-success/90 text-success-foreground h-8"
                onClick={() => onApprove(artisan.id)}
                disabled={isActing}
                aria-label={`Approuver la vérification de ${formatName(artisan)}`}
              >
                <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5 mr-1.5" />
                Approuver
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/5 h-8"
                onClick={() => onReject(artisan)}
                disabled={isActing}
                aria-label={`Rejeter les documents de ${formatName(artisan)}`}
              >
                <XCircle aria-hidden="true" className="h-3.5 w-3.5 mr-1.5" />
                Rejeter
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Artisan Row (existing inscription queue) ─────────────────────────────────

function ArtisanRow({
  artisan,
  canAct,
  onApprove,
  onSuspend,
  onContact,
  isActing,
}: {
  artisan: ArtisanItem;
  canAct: boolean;
  onApprove: (id: string) => void;
  onSuspend: (artisan: ArtisanItem) => void;
  onContact: (artisan: ArtisanItem) => void;
  isActing: boolean;
}) {
  return (
    <Card>
      <CardContent className="py-4 px-5">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
            {(artisan.companyName[0] ?? "A").toUpperCase()}
          </div>

          {/* Main info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <div className="font-medium text-sm">{formatName(artisan)}</div>
                <div className="text-xs text-muted-foreground">{artisan.companyName}</div>
              </div>
              <VerificationStatusBadge status={deriveStatus(artisan)} />
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Mail aria-hidden="true" className="h-3 w-3 shrink-0" />
                <span className="truncate">{artisan.User.email}</span>
              </div>
              {artisan.User.phone && (
                <div className="flex items-center gap-1">
                  <Phone aria-hidden="true" className="h-3 w-3 shrink-0" />
                  <span>{artisan.User.phone}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <MapPin aria-hidden="true" className="h-3 w-3 shrink-0" />
                <span>{artisan.serviceCity} (+{artisan.serviceRadiusKm} km)</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar aria-hidden="true" className="h-3 w-3 shrink-0" />
                <span>
                  Inscrit le{" "}
                  {new Date(artisan.User.createdAt).toLocaleDateString("fr-FR", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Building2 aria-hidden="true" className="h-3 w-3 shrink-0" />
                <span>
                  {artisan.siret ? `SIRET: ${artisan.siret}` : "SIRET non renseigné"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Shield aria-hidden="true" className="h-3 w-3 shrink-0" />
                <span>
                  Décennale:{" "}
                  {artisan.hasDecennale === "YES"
                    ? "Oui"
                    : artisan.hasDecennale === "IN_PROGRESS"
                    ? "En cours"
                    : "Non"}
                </span>
              </div>
            </div>

            {/* Specialties */}
            <div className="mt-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Corps de métier :</span>{" "}
              {getSpecialtiesLabel(artisan.specialties)}
            </div>
          </div>

          {/* Actions */}
          {canAct && (
            <div className="flex flex-col gap-2 shrink-0">
              <Button
                size="sm"
                className="bg-success hover:bg-success/90 text-success-foreground h-8"
                onClick={() => onApprove(artisan.id)}
                disabled={isActing}
                aria-label={`Valider l'inscription de ${formatName(artisan)}`}
              >
                <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5 mr-1.5" />
                Valider
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/5 h-8"
                onClick={() => onSuspend(artisan)}
                disabled={isActing}
                aria-label={`Suspendre l'inscription de ${formatName(artisan)}`}
              >
                <XCircle aria-hidden="true" className="h-3.5 w-3.5 mr-1.5" />
                Suspendre
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                onClick={() => onContact(artisan)}
                disabled={isActing}
                aria-label={`Contacter ${formatName(artisan)}`}
              >
                <Send aria-hidden="true" className="h-3.5 w-3.5 mr-1.5" />
                Contacter
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type Tab = "inscriptions" | "documents" | "rc_pro";

export default function ArtisansModeration() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);

  const [activeTab, setActiveTab] = useState<Tab>("inscriptions");
  const [page, setPage] = useState(1);
  const [docsPage, setDocsPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Artisan suspend dialog
  const [rejectDialog, setRejectDialog] = useState<{
    open: boolean;
    artisan: ArtisanItem | null;
    mode: "suspend" | "reject";
  }>({ open: false, artisan: null, mode: "suspend" });
  const [rejectReason, setRejectReason] = useState("");

  // Email artisan dialog
  const [emailDialog, setEmailDialog] = useState<{
    open: boolean;
    artisanId: string;
    artisanName: string;
    artisanEmail: string;
  }>({ open: false, artisanId: "", artisanName: "", artisanEmail: "" });
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  // Document reject dialog
  const [docRejectDialog, setDocRejectDialog] = useState<{
    open: boolean;
    doc: ArtisanDocument | null;
    artisanName: string;
  }>({ open: false, doc: null, artisanName: "" });
  const [docRejectReason, setDocRejectReason] = useState("");

  // ── Inscriptions query ──
  const { data, isLoading, refetch } = trpc.admin.travauxbtp.listPendingArtisans.useQuery({
    page,
    limit: 20,
    search: search || undefined,
  });

  // ── Document verifications query ──
  const { data: docsData, isLoading: docsLoading, refetch: refetchDocs } =
    trpc.admin.travauxbtp.listPendingVerifications.useQuery(
      { page: docsPage, limit: 20 },
      { enabled: activeTab === "documents" }
    );

  // ── Mutations ──
  const approveMutation = trpc.admin.travauxbtp.approveArtisan.useMutation({
    onSuccess: () => {
      toast.success("Artisan validé — email de confirmation envoyé");
      refetch();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de la validation"),
  });

  const suspendMutation = trpc.admin.travauxbtp.suspendArtisan.useMutation({
    onSuccess: () => {
      const msg =
        rejectDialog.mode === "reject"
          ? "Documents rejetés — artisan notifié"
          : "Artisan suspendu";
      toast.success(msg);
      setRejectDialog({ open: false, artisan: null, mode: "suspend" });
      setRejectReason("");
      refetch();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de l'action"),
  });

  const sendEmailMutation = trpc.admin.travauxbtp.sendArtisanEmail.useMutation({
    onSuccess: () => {
      toast.success("Email envoyé à l'artisan");
      setEmailDialog({ open: false, artisanId: "", artisanName: "", artisanEmail: "" });
      setEmailSubject("");
      setEmailBody("");
    },
    onError: (err) => toast.error(err.message || "Erreur lors de l'envoi"),
  });

  const approveDocMutation = trpc.admin.travauxbtp.approveDocument.useMutation({
    onSuccess: (result) => {
      const msg = result.allApproved
        ? "Document approuvé — profil vérifié !"
        : "Document approuvé";
      toast.success(msg);
      refetchDocs();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de l'approbation"),
  });

  const rejectDocMutation = trpc.admin.travauxbtp.rejectDocument.useMutation({
    onSuccess: () => {
      toast.success("Document rejeté — artisan notifié");
      setDocRejectDialog({ open: false, doc: null, artisanName: "" });
      setDocRejectReason("");
      refetchDocs();
    },
    onError: (err) => toast.error(err.message || "Erreur lors du rejet"),
  });

  // ── RC Pro queries & mutations ──
  const [rcProPage, setRcProPage] = useState(1);
  const { data: rcProData, isLoading: rcProLoading, refetch: refetchRcPro } =
    trpc.proProfile.getPendingRcPro.useQuery(undefined, {
      enabled: activeTab === "rc_pro",
    });

  const verifyRcProMutation = trpc.proProfile.verifyRcPro.useMutation({
    onSuccess: (result) => {
      const msg = result.isVerified
        ? "RC Pro validée — badge artisan vérifié attribué !"
        : "RC Pro validée — en attente de validation SIRET";
      toast.success(msg);
      refetchRcPro();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de la validation RC Pro"),
  });

  const rejectRcProMutation = trpc.proProfile.rejectRcPro.useMutation({
    onSuccess: () => {
      toast.success("RC Pro rejetée — artisan notifié");
      setRcProRejectDialog({ open: false, proId: null, proName: "" });
      setRcProRejectReason("");
      refetchRcPro();
    },
    onError: (err) => toast.error(err.message || "Erreur lors du rejet"),
  });

  // RC Pro reject dialog
  const [rcProRejectDialog, setRcProRejectDialog] = useState<{
    open: boolean;
    proId: string | null;
    proName: string;
  }>({ open: false, proId: null, proName: "" });
  const [rcProRejectReason, setRcProRejectReason] = useState("");

  const isActing =
    approveMutation.isPending ||
    suspendMutation.isPending ||
    sendEmailMutation.isPending ||
    approveDocMutation.isPending ||
    rejectDocMutation.isPending ||
    verifyRcProMutation.isPending ||
    rejectRcProMutation.isPending;

  function handleSearch() {
    setSearch(searchInput);
    setPage(1);
  }

  function handleApprove(artisanProfileId: string) {
    approveMutation.mutate({ artisanProfileId });
  }

  function handleOpenSuspend(artisan: ArtisanItem) {
    setRejectDialog({ open: true, artisan, mode: "suspend" });
    setRejectReason("");
  }

  function handleOpenReject(artisan: ArtisanItem) {
    setRejectDialog({ open: true, artisan, mode: "reject" });
    setRejectReason("");
  }

  function handleConfirmReject() {
    if (!rejectDialog.artisan) return;
    suspendMutation.mutate({
      artisanProfileId: rejectDialog.artisan.id,
      reason: rejectReason || undefined,
    });
  }

  function handleOpenDocReject(doc: ArtisanDocument, artisanName: string) {
    setDocRejectDialog({ open: true, doc, artisanName });
    setDocRejectReason("");
  }

  function handleConfirmDocReject() {
    if (!docRejectDialog.doc || !docRejectReason.trim()) return;
    rejectDocMutation.mutate({
      documentId: docRejectDialog.doc.id,
      reason: docRejectReason.trim(),
    });
  }

  const items = (data?.items ?? []) as ArtisanItem[];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const docsItems = (docsData?.items ?? []) as PendingVerificationItem[];
  const docsTotal = docsData?.total ?? 0;
  const docsTotalPages = docsData?.totalPages ?? 1;

  const isSuspendMode = rejectDialog.mode === "suspend";

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <UserCheck aria-hidden="true" className="h-6 w-6 text-primary" />
            Modération artisans
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {activeTab === "inscriptions"
              ? `${total} dossier${total > 1 ? "s" : ""} en attente de validation`
              : activeTab === "documents"
                ? `${docsTotal} dossier${docsTotal > 1 ? "s" : ""} avec documents à vérifier`
                : `${rcProData?.length ?? 0} RC Pro en attente de validation`}
          </p>
        </div>
      </div>

      {/* Documents expiration link */}
      <Link
        to="/travauxbtp/documents"
        className="flex items-center gap-2 mb-4 px-3 py-2 rounded-md bg-muted/50 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        <FileText className="h-4 w-4" />
        Vue globale des documents et alertes expiration
        <ExternalLink className="h-3 w-3 ml-auto" />
      </Link>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-border">
        <button
          type="button"
          onClick={() => { setActiveTab("inscriptions"); setPage(1); }}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "inscriptions"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Inscriptions en attente
          {total > 0 && (
            <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
              {total}
            </Badge>
          )}
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab("documents"); setDocsPage(1); }}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "documents"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Validation documents
          {docsTotal > 0 && (
            <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
              {docsTotal}
            </Badge>
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("rc_pro")}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "rc_pro"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Shield className="h-3.5 w-3.5 inline mr-1.5" aria-hidden="true" />
          Validation RC Pro
          {(rcProData?.length ?? 0) > 0 && (
            <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
              {rcProData!.length}
            </Badge>
          )}
        </button>
      </div>

      {/* ── Tab: Inscriptions ── */}
      {activeTab === "inscriptions" && (
        <>
          {/* Search */}
          <div className="flex gap-2 mb-5 max-w-sm">
            <div className="relative flex-1">
              <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                aria-label="Rechercher un artisan"
                placeholder="Nom, email, société..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="pl-9"
              />
            </div>
            <Button variant="outline" onClick={handleSearch} size="sm">
              Rechercher
            </Button>
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Chargement…
            </div>
          )}

          {!isLoading && items.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                {search
                  ? "Aucun artisan trouvé pour cette recherche"
                  : "Aucun dossier en attente — tout est à jour !"}
              </CardContent>
            </Card>
          )}

          {!isLoading && items.length > 0 && (
            <div className="space-y-3">
              {items.map((artisan) => (
                <ArtisanRow
                  key={artisan.id}
                  artisan={artisan}
                  canAct={canAct}
                  onApprove={handleApprove}
                  onSuspend={handleOpenSuspend}
                  onContact={(a) => {
                    setEmailDialog({
                      open: true,
                      artisanId: a.id,
                      artisanName: a.companyName,
                      artisanEmail: a.User.email,
                    });
                  }}
                  isActing={isActing}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 text-sm text-muted-foreground">
              <span>
                Page {page} sur {totalPages} — {total} résultat{total > 1 ? "s" : ""}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Page précédente">
                  <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Page suivante">
                  <ChevronRight aria-hidden="true" className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Tab: Validation documents ── */}
      {activeTab === "documents" && (
        <>
          {docsLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Chargement…
            </div>
          )}

          {!docsLoading && docsItems.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                Aucun document en attente de vérification — tout est à jour !
              </CardContent>
            </Card>
          )}

          {!docsLoading && docsItems.length > 0 && (
            <div className="space-y-4">
              {docsItems.map((item) => (
                <DocumentVerificationCard
                  key={item.id}
                  item={item}
                  canAct={canAct}
                  onApproveDoc={(docId) => approveDocMutation.mutate({ documentId: docId })}
                  onRejectDoc={handleOpenDocReject}
                  isActing={isActing}
                />
              ))}
            </div>
          )}

          {docsTotalPages > 1 && (
            <div className="flex items-center justify-between mt-6 text-sm text-muted-foreground">
              <span>
                Page {docsPage} sur {docsTotalPages} — {docsTotal} résultat{docsTotal > 1 ? "s" : ""}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={docsPage <= 1} onClick={() => setDocsPage((p) => p - 1)} aria-label="Page précédente">
                  <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={docsPage >= docsTotalPages} onClick={() => setDocsPage((p) => p + 1)} aria-label="Page suivante">
                  <ChevronRight aria-hidden="true" className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Tab: Validation RC Pro ── */}
      {activeTab === "rc_pro" && (
        <div className="space-y-4">
          {rcProLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Chargement…
            </div>
          )}

          {!rcProLoading && (!rcProData || rcProData.length === 0) && (
            <Card>
              <CardContent className="py-12 text-center">
                <Shield className="h-10 w-10 text-success mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Aucune RC Pro en attente de validation — tout est à jour !
                </p>
              </CardContent>
            </Card>
          )}

          {!rcProLoading && rcProData && rcProData.length > 0 && (
            <div className="space-y-3">
              {rcProData.map((profile) => {
                const name = [profile.user.firstName, profile.user.lastName]
                  .filter(Boolean)
                  .join(" ") || profile.user.email;
                const initial = (profile.user.firstName?.[0] || profile.user.email[0] || "?").toUpperCase();

                return (
                  <Card key={profile.id}>
                    <CardContent className="py-5 px-5">
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
                            {initial}
                          </div>
                          <div>
                            <div className="font-medium text-sm">{name}</div>
                            <div className="text-xs text-muted-foreground">
                              {profile.companyName}
                              {profile.siret && (
                                <span className="ml-2 text-muted-foreground/60">
                                  SIRET {profile.siret}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {profile.user.email}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {profile.serviceCity}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Badge className="bg-warning/10 text-warning border-warning/20 shrink-0">
                          <Clock className="h-3 w-3 mr-1" aria-hidden="true" />
                          RC Pro en attente
                        </Badge>
                      </div>

                      {/* RC Pro document */}
                      <div className="rounded-lg border border-border divide-y divide-border mb-3">
                        <div className="flex items-center gap-3 px-4 py-3">
                          <FileText aria-hidden="true" className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">
                              Attestation RC Pro
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              Soumis le{" "}
                              {profile.rcProDocument
                                ? new Date(profile.rcProDocument.uploadedAt).toLocaleDateString("fr-FR", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "—"}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className="bg-warning/10 text-warning border-warning/20 text-xs">
                              <Clock className="h-3 w-3 mr-1" />
                              En attente
                            </Badge>
                            {profile.rcProDocument && (
                              <a
                                href={profile.rcProDocument.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-foreground"
                                title="Voir le document"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            )}
                            {canAct && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-success hover:bg-success/90 text-success-foreground h-7 px-2.5"
                                  disabled={isActing}
                                  onClick={() =>
                                    verifyRcProMutation.mutate({ proId: profile.userId })
                                  }
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                                  Valider
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-destructive text-destructive h-7 px-2.5"
                                  disabled={isActing}
                                  onClick={() => {
                                    setRcProRejectDialog({
                                      open: true,
                                      proId: profile.userId,
                                      proName: name,
                                    });
                                    setRcProRejectReason("");
                                  }}
                                >
                                  <XCircle className="h-3.5 w-3.5 mr-1" />
                                  Rejeter
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Reject / Suspend dialog (inscriptions tab) */}
      <Dialog
        open={rejectDialog.open}
        onOpenChange={(open) => setRejectDialog((s) => ({ ...s, open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isSuspendMode ? "Suspendre l'inscription" : "Rejeter les documents"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {isSuspendMode ? (
              <>
                Vous êtes sur le point de suspendre l'artisan{" "}
                <span className="font-medium text-foreground">
                  {rejectDialog.artisan ? formatName(rejectDialog.artisan) : ""}
                </span>
                . Cette action désactivera le compte et empêchera l'accès à TravauxBTP.
              </>
            ) : (
              <>
                Les documents de{" "}
                <span className="font-medium text-foreground">
                  {rejectDialog.artisan ? formatName(rejectDialog.artisan) : ""}
                </span>{" "}
                seront rejetés. L'artisan sera notifié et pourra soumettre à nouveau ses justificatifs.
              </>
            )}
          </p>
          <div>
            <Label className="text-xs">
              {isSuspendMode ? "Raison (optionnel)" : "Motif de rejet (optionnel)"}
            </Label>
            <Textarea
              placeholder={
                isSuspendMode
                  ? "Informations manquantes, documents invalides..."
                  : "SIRET introuvable, assurance expirée, Kbis trop ancien..."
              }
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialog({ open: false, artisan: null, mode: "suspend" })}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmReject}
              disabled={suspendMutation.isPending}
            >
              {suspendMutation.isPending
                ? isSuspendMode
                  ? "Suspension…"
                  : "Rejet…"
                : isSuspendMode
                ? "Confirmer la suspension"
                : "Confirmer le rejet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document reject dialog */}
      <Dialog
        open={docRejectDialog.open}
        onOpenChange={(open) => setDocRejectDialog((s) => ({ ...s, open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rejeter le document</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Rejet du document{" "}
            <span className="font-medium text-foreground">
              {docRejectDialog.doc ? (DOCUMENT_TYPE_LABELS[docRejectDialog.doc.type] ?? docRejectDialog.doc.type) : ""}
            </span>{" "}
            de{" "}
            <span className="font-medium text-foreground">{docRejectDialog.artisanName}</span>.
            L'artisan sera notifié par email et pourra soumettre à nouveau ce document.
          </p>
          <div>
            <Label className="text-xs">Motif de rejet <span className="text-destructive">*</span></Label>
            <Textarea
              placeholder="SIRET introuvable, assurance expirée, document illisible..."
              rows={3}
              value={docRejectReason}
              onChange={(e) => setDocRejectReason(e.target.value)}
            />
            {docRejectReason.trim() === "" && (
              <p className="text-xs text-destructive mt-1">Le motif est obligatoire</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDocRejectDialog({ open: false, doc: null, artisanName: "" })}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDocReject}
              disabled={rejectDocMutation.isPending || !docRejectReason.trim()}
            >
              {rejectDocMutation.isPending ? "Rejet…" : "Confirmer le rejet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RC Pro reject dialog */}
      <Dialog
        open={rcProRejectDialog.open}
        onOpenChange={(open) => setRcProRejectDialog((s) => ({ ...s, open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rejeter la RC Pro</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Rejet de la RC Pro de{" "}
            <span className="font-medium text-foreground">{rcProRejectDialog.proName}</span>.
            L'artisan sera notifié et pourra soumettre un nouveau document.
          </p>
          <div>
            <Label className="text-xs">
              Motif de rejet <span className="text-destructive">*</span>
            </Label>
            <Textarea
              placeholder="Document illisible, RC Pro expirée, activité non couverte..."
              rows={3}
              value={rcProRejectReason}
              onChange={(e) => setRcProRejectReason(e.target.value)}
            />
            {rcProRejectReason.trim() === "" && (
              <p className="text-xs text-destructive mt-1">Le motif est obligatoire</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setRcProRejectDialog({ open: false, proId: null, proName: "" })
              }
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!rcProRejectDialog.proId || !rcProRejectReason.trim()) return;
                rejectRcProMutation.mutate({
                  proId: rcProRejectDialog.proId,
                  motif: rcProRejectReason.trim(),
                });
              }}
              disabled={rejectRcProMutation.isPending || !rcProRejectReason.trim()}
            >
              {rejectRcProMutation.isPending ? "Rejet…" : "Confirmer le rejet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email artisan dialog */}
      <Dialog
        open={emailDialog.open}
        onOpenChange={(open) => setEmailDialog((s) => ({ ...s, open }))}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Contacter l'artisan</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Envoyer un email à{" "}
            <span className="font-medium text-foreground">{emailDialog.artisanName}</span>{" "}
            ({emailDialog.artisanEmail})
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Objet <span className="text-destructive">*</span></Label>
              <Input
                placeholder="Objet de l'email..."
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Message <span className="text-destructive">*</span></Label>
              <Textarea
                placeholder="Contenu de l'email..."
                rows={6}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEmailDialog({ open: false, artisanId: "", artisanName: "", artisanEmail: "" });
                setEmailSubject("");
                setEmailBody("");
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                sendEmailMutation.mutate({
                  artisanProfileId: emailDialog.artisanId,
                  subject: emailSubject.trim(),
                  body: emailBody.trim(),
                });
              }}
              disabled={sendEmailMutation.isPending || !emailSubject.trim() || !emailBody.trim()}
            >
              <Send className="h-4 w-4 mr-2" />
              {sendEmailMutation.isPending ? "Envoi…" : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
